/**
 * Permission codes the Payroll screens declare an interest in.
 *
 * These are the dot-strings `internal/payroll/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`.
 *
 * **The run lifecycle is a segregation-of-duties chain and no step is inferred
 * from another.** Calculating a run, reviewing its exceptions, approving it,
 * posting it to the ledger and disbursing it are five separately permissioned
 * decisions, and the screens render only the ones the user actually holds. A row
 * of disabled buttons implying somebody is one promotion away from paying a
 * payroll they prepared themselves is exactly what §6 exists to prevent.
 *
 * `runDisburse` is the one that moves money. It declares `RequiresMFA` — the
 * same treatment `ap.payment.execute` gets — and it also gates the **bank file**,
 * because producing that file decrypts every beneficiary's account number.
 *
 * Three codes belong to other modules and are listed because these screens
 * cannot do their job without them:
 *
 *  - `org.company.read` — a run is raised for a company, and the company's base
 *    currency is what its totals are stated in.
 *  - `org.account.read` — the net-pay payable account a run credits. The picker
 *    has to name a real liability account from the company's chart; a run
 *    created against a guessed id fails at post time, which is far too late.
 *  - `hr.employee.read` — a payslip names an employee, and the manual-line
 *    dialog picks one. `internal/payroll` never reads HR's tables, but these
 *    screens legitimately read both APIs.
 */

export const PAYROLL_PERMISSIONS = {
  componentRead: 'payroll.component.read',
  componentManage: 'payroll.component.manage',

  runRead: 'payroll.run.read',
  runCreate: 'payroll.run.create',
  /** Calculate, recalculate, and manage the run's manual override lines. */
  runCalculate: 'payroll.run.calculate',
  runReview: 'payroll.run.review',
  runApprove: 'payroll.run.approve',
  runPost: 'payroll.run.post',
  /** Moves money. `RequiresMFA`, and gates the bank file. */
  runDisburse: 'payroll.run.disburse',
  runCancel: 'payroll.run.cancel',

  payslipRead: 'payroll.payslip.read',

  /* ------------------------------------------------- other modules' codes */

  companyRead: 'org.company.read',
  accountRead: 'org.account.read',
  employeeRead: 'hr.employee.read',
} as const

export type PayrollPermission = (typeof PAYROLL_PERMISSIONS)[keyof typeof PAYROLL_PERMISSIONS]
